import * as React from 'react';

import { Box, ClickAwayListener, Divider, IconButton, MenuItem } from '@mui/material';

import { ExpandLess, ExpandMore, MoreVert } from '@mui/icons-material';

import { observer } from 'mobx-react';

import { StyledTextField } from 'components/Input/styles';

import { useStore } from 'utils/hooks/store';

import { Header, SectionInformation, StyledPopover } from '../styles';

const TextFiledStyle = {
  '& .MuiInputBase-root': {
    maxHeight: '36px',
    paddingTop: '6px',
    paddingBottom: '6px',
    background: 'white',
  },
};

type SectionHeaderProps = {
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  expanded: boolean;
  ruleId: string;
};

const SectionHeader: React.FC<SectionHeaderProps> = ({ setExpanded, expanded, ruleId }): React.ReactElement => {
  const { assignmentRules } = useStore();

  // Find rule in both regular and pipeline rules
  const rule =
    assignmentRules.assignmentRules.find(({ uuid }) => uuid === ruleId) ||
    assignmentRules.assignmentRulesPipelines.find(({ uuid }) => uuid === ruleId);

  const [data, setData] = React.useState({
    name: rule?.name,
    description: rule?.description,
  });

  const [editing, setEditing] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleExpandClick = (): void => {
    setExpanded((prevState) => !prevState);
  };

  const onClickThreeDots = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const onClickAway = (): void => {
    // update only the title and description of edited section
    if (editing) {
      setEditing(false);
      assignmentRules.updateRuleGeneric(ruleId, { name: data.name, description: data.description });
    }
  };

  const onEdit = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    setEditing(true);
  };

  const addRule = (): void => {
    setAnchorEl(null);
    // Check if current rule is a pipeline rule to determine which method to use
    const isPipelineRule = assignmentRules.assignmentRulesPipelines.some(({ uuid }) => uuid === ruleId);
    if (isPipelineRule) {
      assignmentRules.createEmptyRulePipeline(ruleId);
    } else {
      assignmentRules.createEmptyRule(ruleId);
    }
  };

  const onDelete = (): void => {
    setAnchorEl(null);
    assignmentRules.deleteRuleGeneric(ruleId);
  };

  return (
    <>
      <ClickAwayListener onClickAway={onClickAway}>
        <Header onClick={handleExpandClick} role="button" tabIndex={0} $expanded={expanded} className="sectionHeader">
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%' }}>
            {expanded ? (
              <ExpandLess aria-labelledby="show less" fillOpacity="0.54" sx={{ width: '16px', height: '16px' }} />
            ) : (
              <ExpandMore aria-labelledby="show more" fillOpacity="0.54" sx={{ width: '16px', height: '16px' }} />
            )}
            <SectionInformation onClick={onEdit}>
              {editing ? (
                <>
                  <StyledTextField
                    fullWidth
                    autoFocus
                    size="small"
                    value={data.name}
                    sx={TextFiledStyle}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={(e) => e.key === 'Enter' && onClickAway()}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                  />
                  <StyledTextField
                    fullWidth
                    size="small"
                    sx={TextFiledStyle}
                    value={data.description}
                    placeholder="Description (Optional)"
                    onKeyDown={(e) => e.key === 'Enter' && onClickAway()}
                    onChange={(e) => setData({ ...data, description: e.target.value })}
                  />
                </>
              ) : (
                <>
                  <Box component="h1" sx={{ margin: 0 }}>
                    {data.name}
                  </Box>
                  {data.description && data.description.length > 0 && (
                    <Box component="p" sx={{ margin: 0 }}>
                      {data.description}
                    </Box>
                  )}
                </>
              )}
            </SectionInformation>
          </Box>
          <IconButton onClick={onClickThreeDots}>
            <MoreVert />
          </IconButton>
        </Header>
      </ClickAwayListener>
      <StyledPopover
        disableScrollLock
        id="popover-section-header"
        data-testid="popover-section-header"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem id="delete-section-button" onClick={onDelete}>
          Delete
        </MenuItem>
        <Divider sx={{ m: 0 }} />
        <MenuItem id="add-question-button" onClick={addRule}>
          Add another rule
        </MenuItem>
      </StyledPopover>
    </>
  );
};

export default observer(SectionHeader);
